import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { CheckCircle2, FileSignature, Loader2 } from "lucide-react";
import Modal from "@/components/Modal";
import SignaturePad from "@/components/student/SignaturePad";
import companyChopUrl from "@/assets/images/we2techChop.png";
import { uploadAgreementPdf } from "@/services/middleware/uploadAgreementPdf";
import {
  getAgreementDateString,
  getAgreementQuoteNumber,
  isUsableBrNumber,
  maskHkid,
  resolveAgreementSections,
  type AgreementFrontPageInfo,
  type AgreementSponsorTask,
} from "@/data/sponsorshipAgreement";
import type { SponsorProviderInfo } from "@/services/api/sponsor/getSponsors";

/**
 * The company chop, inlined as a data URI.
 *
 * The finalized agreement is rendered to PDF by the API from this HTML, so the
 * document must be self-contained: the API cannot resolve a relative web asset
 * path, and its request interception deliberately blocks unknown hosts. Vite's
 * default 4 KB inline limit leaves this 41 KB PNG as a separate file, so it is
 * fetched and converted once and the result reused for every signing.
 *
 * RN inlines the same bytes at runtime from its bundled copy
 * (`apps/uninfo/src/assets/images/we2techChop.png`); both platforms ship
 * byte-identical PNGs so the two renderings match.
 */
let companyChopPromise: Promise<string> | null = null;
const loadCompanyChop = (): Promise<string> => {
  if (!companyChopPromise) {
    companyChopPromise = fetch(companyChopUrl)
      .then((response) => response.blob())
      .then(
        (blob) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result));
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
          }),
      )
      .catch((err) => {
        // Do not cache a failure: the next signing attempt should retry.
        companyChopPromise = null;
        throw err;
      });
  }
  return companyChopPromise;
};

interface StudentSponsorAgreementModalProps {
  visible: boolean;
  onClose: () => void;
  /** Resolved when the student signs; receives the Cloudinary URL of the PDF. */
  onSigned: (signedAgreementPdfUrl: string) => void;
  sponsorName?: string;
  sponsorDescription?: string;
  /**
   * The registered identity of the company offering the sponsor, from the
   * owner's business profile. This is the party the company chop belongs to, so
   * it — not `sponsorName`, which is a product title — is what the left signature
   * cell has to name.
   */
  provider?: SponsorProviderInfo | null;
  sponsorTasks?: AgreementSponsorTask[];
  activityName: string;
  activityDate: string;
  officialIG: string;
  committeeIGs: string[];
  societyName?: string;
  societyChopUrl?: string;
  redeemType: "personal" | "society";
  /** Self-reported participant count for this redemption. */
  participantsQuantity?: number;
  /** Quantity the student chose, in packs as priced. */
  quantity?: number;
  /** Ceiling derived from participants and the unit price. */
  maxQuantity?: number;
  /** Upload a society chop, or replace the one the society already has */
  onUploadChop?: () => void;
  /** Whether chop upload is in progress */
  chopUploading?: boolean;
}

/**
 * Web port of the RN `ModalAgreementSignature`: the same agreement sections and
 * the same scroll-to-bottom gate, with the signature drawn in a full-screen
 * overlay and committed on its Confirm, and a bottom action row holding Cancel
 * on the left and the chop control next to Sign Now / Finalize on the right.
 *
 * The signed document text is deliberately hardcoded English rather than
 * i18n: it is a signed legal document, so its two renderings (web and RN)
 * must be byte-identical for the same input, which a translation layer cannot
 * guarantee. UI chrome around the agreement stays translated.
 *
 * Deviations from mobile: the society chop is required to finalize (mobile
 * offers it as optional and hides it once a chop is on file), the footer keeps a
 * chop preview and a "Replace society chop" control that mobile lacks (a society
 * cannot fix a poor scan from the phone flow), and the finalized agreement is a
 * self-contained `data:text/html` document (opens / prints from a tab) rather
 * than a PDF uploaded to Cloudinary, because web has no PDF generator.
 */
const StudentSponsorAgreementModal: React.FC<
  StudentSponsorAgreementModalProps
> = ({
  visible,
  onClose: dismissAgreement,
  onSigned,
  sponsorName,
  sponsorDescription,
  provider,
  sponsorTasks,
  activityName,
  activityDate,
  officialIG,
  committeeIGs,
  societyName,
  societyChopUrl,
  redeemType,
  participantsQuantity,
  quantity,
  maxQuantity,
  onUploadChop: uploadChop,
  chopUploading,
}) => {
  const { t } = useTranslation();
  const student = useSelector((state: any) => state.auth?.student);
  const scrollRef = useRef<HTMLDivElement>(null);
  /**
   * The footer, which is where every control of this dialog lives. It goes
   * `inert` for the length of a finalize: `inert` is one attribute that takes the
   * footer out of the tab order, out of the accessibility tree and out of hit
   * testing at once, so the visual lock, what a screen reader announces and what
   * the keyboard can reach cannot drift apart. `disabled` is not a substitute
   * here — the same `disabled` expressions are the chop's `chopUploading` and
   * Sign Now's `!scrolledToBottom`, which say nothing about a finalize in
   * flight, and a control with no `disabled` cannot be described as disabled.
   */
  const footerRef = useRef<HTMLDivElement>(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  /** The drawing in the overlay that has not been committed yet. */
  const [draftSignature, setDraftSignature] = useState<string | null>(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /** Set when the PDF could not be produced, so nothing is ever stored broken. */
  const [finalizeError, setFinalizeError] = useState<string | null>(null);

  const sections = useMemo(
    () =>
      resolveAgreementSections({
        sponsorName,
        sponsorDescription,
        sponsorTasks,
        activityName,
        activityDate,
      }),
    [sponsorName, sponsorDescription, sponsorTasks, activityName, activityDate],
  );

  /** Front page of the RN agreement: quote reference + From/To party details. */
  const frontPage = useMemo<AgreementFrontPageInfo>(() => {
    const now = new Date();
    // A BR number that is missing or still placeholder data states an
    // identification the company does not have, so the row is left out of the
    // document entirely and the reason is logged for whoever can fix the profile.
    const storedBrNumber = provider?.brNumber?.trim() || undefined;
    const brNumber =
      storedBrNumber && isUsableBrNumber(storedBrNumber) ? storedBrNumber : undefined;
    if (!brNumber) {
      console.warn(
        `[agreement] this sponsor has no usable BR number (stored: ${JSON.stringify(storedBrNumber ?? null)}) - the BR No. row was omitted from the signed agreement. Set business_profiles.br_number to the real Companies Registry number.`,
      );
    }
    return {
      quoteNumber: getAgreementQuoteNumber({
        hkidName: student?.hkid_name,
        now,
      }),
      agreementDate: getAgreementDateString(now),
      sponsorName: sponsorName || undefined,
      // The contact rows are the business profile's: it records a company, so
      // its "Name" is the registered name and its Tel/Email are the switchboard
      // details rather than a named person.
      sponsorContactName: provider?.legalName || undefined,
      sponsorTel: provider?.contactNumber || undefined,
      sponsorEmail: provider?.companyEmail || undefined,
      providerLegalName: provider?.legalName || undefined,
      sponsorBrNumber: brNumber,
      sponsorAddress: provider?.registeredAddress || undefined,
      clubName: redeemType === "society" ? societyName || undefined : undefined,
      clubRepName: student?.hkid_name || undefined,
      clubRepHkid: maskHkid(student?.hkid_number) || undefined,
      clubTel: student?.phone_num ? `${student.phone_num}` : undefined,
      clubEmail: student?.university_email || undefined,
      generatedTime: now.toLocaleTimeString("en-US", { hour12: false }),
    };
  }, [
    student?.hkid_name,
    student?.hkid_number,
    student?.phone_num,
    student?.university_email,
    provider?.legalName,
    provider?.brNumber,
    provider?.registeredAddress,
    provider?.contactNumber,
    provider?.companyEmail,
    sponsorName,
    societyName,
    redeemType,
  ]);

  /**
   * The party named in the company-chop cell.
   *
   * The chop in that cell is we2tech's own corporate seal, so the name over it
   * has to be the legal entity it belongs to. `sponsorName` is a product title
   * and `clubName` is the student, so neither identifies this party — but a cell
   * with no name at all is a signature nobody can be held to, which is the worse
   * of the two, so the product name stands in and the gap is logged for an
   * operator to fill in the business profile.
   */
  const providerPartyName = useMemo(() => {
    const legalName = provider?.legalName?.trim();
    if (legalName) return legalName;
    console.warn(
      "[agreement] this sponsor has no business profile company name - the company chop's cell falls back to the product name. Set business_profiles.company_name so the agreement names its counterparty.",
    );
    return sponsorName || "-";
  }, [provider?.legalName, sponsorName]);

  useEffect(() => {
    if (visible) {
      setScrolledToBottom(false);
      setSignature(null);
      setDraftSignature(null);
      setShowSignaturePad(false);
      setAccepted(false);
      setSubmitting(false);
      setFinalizeError(null);
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }
  }, [visible]);

  // The whole dialog is inert while a finalize is in flight, so the attribute has
  // to follow `submitting` rather than the other way round — and it is applied as
  // a DOM attribute rather than as a React prop because React 18 does not know
  // `inert` and would render it as an unknown attribute instead.
  useEffect(() => {
    footerRef.current?.toggleAttribute("inert", submitting);
  }, [submitting]);

  const filledCommitteeIGs = committeeIGs
    .map((ig) => ig.trim())
    .filter((ig) => ig && ig !== "@");

  /**
   * A society chop is not optional on web: it is the one part of the agreement
   * the signer cannot produce by typing, and the finalized document embeds it.
   * Derived rather than tracked, because a chop uploaded in this session comes
   * back through `societyChopUrl` — the redeem modal adopts the stored URL into
   * `selectedSociety` — so there is nothing extra to keep in step.
   */
  const chopReady = redeemType !== "society" || Boolean(societyChopUrl);

  const lineQuantity =
    typeof quantity === "number" && Number.isFinite(quantity) ? quantity : 0;

  const summary = [
    { label: "Activity name for sponsor", value: activityName },
    { label: "Activity date (e.g. 2026-04-01)", value: activityDate },
    { label: "Sponsor", value: sponsorName || "-" },
    { label: "Official society Instagram account", value: officialIG },
    // Committee accounts are society-only (see the redeem modal), so on a
    // personal redemption this row would print an empty value. Gated to match
    // the RN `buildAgreementHtml`.
    ...(filledCommitteeIGs.length > 0
      ? [
          {
            label: "Committee IG Accounts",
            value: filledCommitteeIGs.join(", "),
          },
        ]
      : []),
    ...(redeemType === "society"
      ? [{ label: "Society", value: societyName || "-" }]
      : []),
  ];

  /**
   * The two front-page columns as the student reads them on screen, kept in step
   * with the PDF's own tables: the BR number and registered address are listed
   * only when they are real, so the preview cannot show an identification the
   * signed document omits.
   */
  const frontPageColumns: { title: string; rows: [string, string | undefined][] }[] = [
    {
      title: "Sponsor Provider",
      rows: [
        ["From", frontPage.sponsorName],
        ["Name", frontPage.sponsorContactName],
        ...(frontPage.sponsorBrNumber
          ? ([["BR No.", frontPage.sponsorBrNumber]] as [string, string][])
          : []),
        ...(frontPage.sponsorAddress
          ? ([["Registered Address", frontPage.sponsorAddress]] as [string, string][])
          : []),
        ["Tel", frontPage.sponsorTel],
        ["Email", frontPage.sponsorEmail],
      ],
    },
    {
      title: "Student",
      rows: [
        ["To", frontPage.clubName],
        ["Full Name of Authorized Representative", frontPage.clubRepName],
        ["HKID", frontPage.clubRepHkid],
        ["Tel", frontPage.clubTel],
        ["Email", frontPage.clubEmail],
      ],
    },
  ];

  const buildAgreementHtml = (companyChop: string) => {
    const escape = (value: string) =>
      value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    const cell = (label: string, value?: string) =>
      `<tr><th>${escape(label)}</th><td>${escape(value || "-")}</td></tr>`;
    const igList = (value: string) => (value.startsWith("@") ? value : `@${value}`);
    // Kept byte-identical to the RN `buildAgreementHtml` items block: no price,
    // no subtotal, no total — the quantity is what the student claimed.
    const lineItemsHtml = `<h2>Sponsorship Items</h2>
<table class="items">
<tr><th>Product</th><th>Quantity (packs as priced)</th></tr>
<tr><td>${escape(sponsorName || "-")}</td><td class="num">${lineQuantity}</td></tr>
</table>
<table class="layout">
<tr><th class="quote">Participants</th><td>${participantsQuantity ?? "-"}</td></tr>
<tr><th class="quote">Max quantity</th><td>${maxQuantity ?? "-"}</td></tr>
</table>`;
    const frontPageHtml = `<table class="layout">
<tr><th class="quote">QUOTE</th><td>${escape(frontPage.quoteNumber)}</td></tr>
<tr><th class="quote">Date</th><td>${escape(frontPage.agreementDate)}</td></tr>
</table>
<table class="layout">
<tr>
<td class="party-cell">
<p class="party">Sponsor Provider</p>
<table>${cell("From", frontPage.sponsorName)}${cell("Name", frontPage.sponsorContactName)}${frontPage.sponsorBrNumber ? cell("BR No.", frontPage.sponsorBrNumber) : ""}${frontPage.sponsorAddress ? cell("Registered Address", frontPage.sponsorAddress) : ""}${cell("Tel", frontPage.sponsorTel)}${cell("Email", frontPage.sponsorEmail)}</table>
</td>
<td class="party-cell">
<p class="party">Student</p>
<table>${cell("To", frontPage.clubName)}${cell("Full Name of Authorized Representative (as shown on HKID)", frontPage.clubRepName)}${cell("HKID", frontPage.clubRepHkid)}${cell("Tel", frontPage.clubTel)}${cell("Email", frontPage.clubEmail)}</table>
</td>
</tr>
</table>
<p class="intro"><i>This agreement is made between the Student and the Sponsor Provider as stated above.</i></p>
<h2>Agreement</h2>
${lineItemsHtml}
${activityName ? `<p><b>Activity Name:</b> ${escape(activityName)}</p>` : ""}
${activityDate ? `<p><b>Activity Date:</b> ${escape(activityDate)}</p>` : ""}
${officialIG ? `<p><b>Official Society IG:</b> ${escape(igList(officialIG))}</p>` : ""}
${filledCommitteeIGs.length > 0 ? `<p><b>Committee IG Accounts:</b> ${escape(filledCommitteeIGs.map(igList).join(", "))}</p>` : ""}
<p class="generated">Generated at ${escape(frontPage.generatedTime)}</p>`;
    const sectionHtml = sections
      .map(
        (section) =>
          `<h2>${escape(`${section.number}. ${section.title}`)}</h2>` +
          (Array.isArray(section.content)
            ? `<ul>${section.content
                .map((item) => `<li>${escape(item)}</li>`)
                .join("")}</ul>`
            : `<p>${escape(String(section.content))}</p>`),
      )
      .join("");
    // The chop images are deliberately un-crossed: the company chop sits in the
    // left (company) cell and the society chop beside the student's signature in
    // the right (society) cell. They used to be the other way round -
    // `societyChopUrl` was rendered in the sponsor's cell, so a society's chop was
    // presented as the company chop and the society had nowhere to put its own.
    //
    // Each `org-name` tracks the chop under it, which is what the label below it
    // says is being signed. The left cell is the company that owns the chop, so
    // it names `providerPartyName` — the registered entity behind the product —
    // and the rows under it are that company's own contact details. The right
    // cell is the society and the student signing for it, so it names `clubName`.
    // `From` stays the product title, which is what it identifies.
    // Kept byte-identical to the RN `buildAgreementHtml` signature block.
    const signatureHtml = `<p>By signing, you agree to the sponsor terms and conditions.</p>
<table class="layout">
<tr>
<td class="party-cell">
<p class="org-label">For and on behalf of</p>
<p class="org-name">${escape(providerPartyName || "-")}</p>
<img src="${companyChop}" alt="company chop" class="chop" />
<p class="chop-label">Signature &amp; Company chop</p>
<table>${cell("Name", frontPage.sponsorContactName)}${cell("Tel", frontPage.sponsorTel)}${cell("Email", frontPage.sponsorEmail)}${cell("Date", frontPage.agreementDate)}</table>
</td>
<td class="party-cell">
<p class="org-label">For and on behalf of</p>
<p class="org-name">${escape(frontPage.clubName || "-")}</p>
<div class="party-signs">
${signature ? `<img class="signature-img" src="${escape(signature)}" alt="signature" />` : '<div class="sign-gap"></div>'}
${societyChopUrl ? `<img class="chop" src="${escape(societyChopUrl)}" alt="society chop" />` : '<div class="chop-gap"></div>'}
</div>
<p class="chop-label">Signature &amp; society chop</p>
<table>${cell("Full Name of Authorized Representative (as shown on HKID)", frontPage.clubRepName)}${cell("Date", frontPage.agreementDate)}</table>
</td>
</tr>
</table>`;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Student Sponsorship Agreement</title><style>
body{font-family:Georgia,'Times New Roman',serif;color:#111;max-width:820px;margin:40px auto;line-height:1.6;padding:0 24px}
h2{font-size:16px;margin-top:26px;border-bottom:1px solid #ccc;padding-bottom:4px}
table{width:100%;border-collapse:collapse;margin:16px 0;font-family:Arial,sans-serif;font-size:13px}
th,td{border:1px solid #bbb;padding:6px 10px;text-align:left} th{width:40%;background:#f6f6f6}
table.layout th.quote{width:20%}
table.layout td.party-cell{vertical-align:top;width:50%}
table table{margin:8px 0} table table th{width:55%}
table.items th{width:auto}
table.items td.num{text-align:right}
.party{font-weight:bold;font-size:15px;margin:0 0 4px}
.org-label{color:#17928B;font-weight:bold;font-size:17px;margin:0}
.org-name{color:#17928B;font-weight:bold;font-size:20px;margin:0 0 8px}
.signature{font-family:'Brush Script MT',cursive;font-size:34px;min-height:40px}
.signature-img{max-height:60px;max-width:260px;display:block;margin:0 auto}
.party-signs{display:flex;align-items:flex-end;justify-content:center;gap:16px}
.party-signs .chop{margin-bottom:0}
.sign-gap{height:60px}
.chop{max-height:90px;display:block;margin:0 auto 8px auto}
.chop-gap{height:90px}
.dash{border-top:1px dashed #222;margin:6px 0}
.chop-label{font-size:12px;color:#444;margin:0 0 6px}
.intro{margin-top:20px} .generated{font-size:11px;color:#666;text-align:right}
</style></head><body>
${frontPageHtml}
${sectionHtml}
${signatureHtml}
</body></html>`;
    return html;
  };

  const handleFinalize = async () => {
    if (submitting) return;
    // The button is already disabled without a chop, but a disabled button is a
    // rendering detail: the handler refuses too, so the requirement cannot be
    // bypassed by reaching it any other way. A missing chop is what the
    // finalized document would otherwise carry a blank line for.
    if (!chopReady) return;
    setSubmitting(true);
    setFinalizeError(null);
    try {
      const companyChop = await loadCompanyChop();
      const now = new Date();
      const stamp = [now.getHours(), now.getMinutes(), now.getSeconds()]
        .map((part) => String(part).padStart(2, "0"))
        .join("");
      // Same `quoteNumber-HHMMSS` public_id RN builds at
      // `ModalAgreementSignature.tsx:298`, so both platforms name their uploads
      // the same way.
      const pdfUrl = await uploadAgreementPdf(
        buildAgreementHtml(companyChop),
        `${frontPage.quoteNumber}-${stamp}`,
      );
      if (!pdfUrl) {
        setFinalizeError(
          t("Failed to generate the signed agreement. Please try again."),
        );
        return;
      }
      onSigned(pdfUrl);
    } catch (err) {
      console.error(`handleFinalize ${err}`);
      setFinalizeError(
        t("Failed to generate the signed agreement. Please try again."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  // The pad publishes on every stroke, so while the overlay is open its value is
  // only a draft: Confirm is what commits it, exactly as the RN
  // `SignatureScreen.onOK` commits on its confirm button. Cancel throws the draft
  // away, which is what keeps an abandoned drawing from overwriting a signature
  // the student already signed with.
  const handleConfirmSignature = () => {
    if (submitting) return;
    if (!draftSignature) return;
    setSignature(draftSignature);
    setDraftSignature(null);
    setShowSignaturePad(false);
  };

  const handleAbandonSignature = () => {
    if (submitting) return;
    setDraftSignature(null);
    setShowSignaturePad(false);
  };

  // Every remaining way into the dialog is closed here rather than at each call
  // site, so one check covers the backdrop, Cancel and anything a future `Modal`
  // close affordance adds (Escape, a close button). The `inert` footer takes the
  // controls out of reach; this is what stops a handler reached anyway — a stale
  // closure over a stale `submitting`, a programmatic call — from acting while
  // the request is in flight.
  const onClose = () => {
    if (submitting) return;
    dismissAgreement();
  };

  const onUploadChop = () => {
    if (submitting) return;
    uploadChop?.();
  };

  return (
    <>
      <Modal
        visible={visible}
        onClose={onClose}
        panelClassName="mx-4 w-full max-w-[44rem]"
      >
        <div
          className="flex max-h-[85vh] w-full flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
          // `aria-busy` is the accessible half of the lock: the footer is inert
          // and cannot be reached, so this is what tells a screen reader the
          // dialog is working rather than empty.
          aria-busy={submitting}
        >
          <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-3">
            <FileSignature size={18} className="text-[#ffa217]" />
            <h2 className="text-sm font-extrabold text-gray-900">
              {t("Student Sponsorship Agreement")}
            </h2>
          </div>

          <div
            ref={scrollRef}
            onScroll={(event) => {
              // Scrolling away mid-finalize is one of the ways to make the frozen
              // dialog feel broken, and `inert` does not stop a wheel or a trackpad
              // over the body. An inline style rather than a class toggle because
              // `overflow-y-auto` stays the declared behaviour of the region and an
              // inline value is what actually wins against the utility class.
              if (submitting) return;
              const el = event.currentTarget;
              if (el.scrollHeight - el.scrollTop - el.clientHeight < 24) {
                setScrolledToBottom(true);
              }
            }}
            // `min-h-0`: a flex item defaults to `min-height: auto`, so the body
            // cannot shrink below its content and the column's `overflow-hidden`
            // clips the overflow instead of letting it scroll. `//` rather than
            // `{/* */}` is required in an opening tag: an expression is not a
            // valid attribute position, and trivia here never reaches the DOM.
            className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4 text-xs leading-5 text-gray-700"
            style={{ overflowY: submitting ? "hidden" : "auto" }}
          >
            <div className="space-y-2">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {frontPageColumns.map((column) => (
                  <div key={column.title}>
                    <p className="mb-1 text-[12px] font-bold text-gray-900">
                      {column.title}
                    </p>
                    <table className="w-full border-collapse text-left">
                      <tbody>
                        {column.rows.map(([label, value]) => (
                          <tr key={label} className="border border-gray-200">
                            <th className="w-1/2 bg-gray-50 px-2 py-1.5 font-semibold text-gray-600">
                              {label}
                            </th>
                            <td className="px-2 py-1.5">{value || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
              <table className="w-full border-collapse text-left">
                <tbody>
                  <tr className="border border-gray-200">
                    <th className="w-1/4 bg-gray-50 px-2 py-1.5 font-semibold text-gray-600">
                      QUOTE
                    </th>
                    <td className="px-2 py-1.5">{frontPage.quoteNumber}</td>
                  </tr>
                  <tr className="border border-gray-200">
                    <th className="w-1/4 bg-gray-50 px-2 py-1.5 font-semibold text-gray-600">
                      Date
                    </th>
                    <td className="px-2 py-1.5">{frontPage.agreementDate}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div>
              <h3 className="mb-1 text-[13px] font-bold text-gray-900">
                Sponsorship Items
              </h3>
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="border border-gray-200">
                    <th className="bg-gray-50 px-2 py-1.5 font-semibold text-gray-600">
                      Product
                    </th>
                    <th className="bg-gray-50 px-2 py-1.5 text-right font-semibold text-gray-600">
                      Quantity (packs as priced)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border border-gray-200">
                    <td className="px-2 py-1.5">{sponsorName || "-"}</td>
                    <td className="px-2 py-1.5 text-right">{lineQuantity}</td>
                  </tr>
                </tbody>
              </table>
              <table className="mt-1 w-full border-collapse text-left">
                <tbody>
                  <tr className="border border-gray-200">
                    <th className="w-3/4 bg-gray-50 px-2 py-1.5 font-semibold text-gray-600">
                      Participants
                    </th>
                    <td className="px-2 py-1.5">
                      {participantsQuantity ?? "-"}
                    </td>
                  </tr>
                  <tr className="border border-gray-200">
                    <th className="w-3/4 bg-gray-50 px-2 py-1.5 font-semibold text-gray-600">
                      Max quantity
                    </th>
                    <td className="px-2 py-1.5">{maxQuantity ?? "-"}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <table className="w-full border-collapse text-left">
              <tbody>
                {summary.map((row) => (
                  <tr key={row.label} className="border border-gray-200">
                    <th className="w-2/5 bg-gray-50 px-2 py-1.5 font-semibold text-gray-600">
                      {row.label}
                    </th>
                    <td className="px-2 py-1.5">{row.value || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {sections.map((section) => (
              <div key={section.title}>
                <h3 className="mb-1 text-[13px] font-bold text-gray-900">
                  {section.number}. {section.title}
                </h3>
                {Array.isArray(section.content) ? (
                  <ul className="list-disc space-y-1 pl-5">
                    {section.content.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p>{String(section.content)}</p>
                )}
              </div>
            ))}
          </div>

          {/*
            The busy line sits outside the inert footer on purpose. Inside it the
            student would be told the dialog is working by a control they are told
            is unavailable; here it reads as the dialog's own status, which is what
            `role="status"` asks for. The Finalize button's own spinner is not
            enough on its own — it explains one control, not the whole window.
          */}
          {submitting ? (
            <p
              role="status"
              className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] font-semibold text-gray-700"
            >
              <Loader2 size={13} className="animate-spin" />
              {t("Finalizing the agreement...")}
            </p>
          ) : null}

          <div
            ref={footerRef}
            className="space-y-3 border-t border-gray-100 px-5 py-4"
          >
            <p className="text-[11px] text-gray-400">
              By signing, you agree to the sponsor terms and conditions.
            </p>
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-700">
              <input
                type="checkbox"
                checked={accepted}
                disabled={submitting}
                onChange={(event) => {
                  // The checkbox is inside the inert footer and carries
                  // `disabled`; this is the third layer, so a change event
                  // delivered some other way cannot accept the agreement on the
                  // student's behalf mid-request.
                  if (submitting) return;
                  setAccepted(event.target.checked);
                }}
                className="h-4 w-4 accent-[#ffa217]"
              />
              {t("I have read and accept the agreement above")}
            </label>

            {/* Deliberate divergence from mobile, which renders neither of these:
                web keeps a picture of what is about to be signed. Both strips sit
                above the action row so the row below holds nothing but actions. */}
            {redeemType === "society" && (societyChopUrl ? (
                <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-2 py-1.5">
                  <span className="shrink-0 text-[11px] font-medium text-gray-500">
                    {t("Society chop")}
                  </span>
                  <img
                    src={societyChopUrl}
                    alt={t("Society chop")}
                    className="h-8 w-auto rounded border border-gray-100 object-contain"
                  />
                </div>
              ) : null)}
            {signature ? (
              <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-2 py-1.5">
                <span className="shrink-0 text-[11px] font-medium text-gray-500">
                  {t("Signed")}
                </span>
                <img
                  src={signature}
                  alt={t("Signed")}
                  className="h-8 w-auto rounded border border-gray-100 object-contain"
                />
              </div>
            ) : null}

            {redeemType === "society" && !societyChopUrl ? (
              <p className="text-[11px] font-semibold text-[#ffa217]">
                {t("A society chop is required to finalize")}
              </p>
            ) : null}

            {finalizeError ? (
              <p className="text-[11px] font-semibold text-red-600">
                {finalizeError}
              </p>
            ) : null}

            {/* One row: Cancel on the left, the chop control and the sign control
                adjacent on the right. The chop sits next to the button it holds
                shut, so a society with nothing on file sees what is missing where
                it acts; both slots are always rendered, so a personal redemption
                leaves the primary button on the right rather than pulling it
                across the row. */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="rounded-xl px-4 py-2 text-sm font-bold text-gray-500"
                >
                  {t("Cancel")}
                </button>
              </div>
              <div className="flex flex-none items-center justify-end gap-2">
                {redeemType === "society" && (societyChopUrl ? (
                    onUploadChop ? (
                      <button
                        type="button"
                        onClick={onUploadChop}
                        disabled={chopUploading}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-[#ffa217] px-3 py-2 text-xs font-semibold text-[#ffa217] transition hover:bg-[#ffa217]/10 disabled:opacity-50"
                      >
                        {chopUploading ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : null}
                        {chopUploading
                          ? t("Uploading...")
                          : t("Replace society chop")}
                      </button>
                    ) : null
                  ) : onUploadChop ? (
                    <button
                      type="button"
                      onClick={onUploadChop}
                      disabled={chopUploading}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-[#ffa217] px-3 py-2 text-xs font-semibold text-[#ffa217] transition hover:bg-[#ffa217]/10 disabled:opacity-50"
                    >
                      {chopUploading ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : null}
                      {chopUploading
                        ? t("Uploading...")
                        : t("Upload society chop")}
                    </button>
                  ) : null)}
                {signature ? (
                  <button
                    type="button"
                    onClick={handleFinalize}
                    disabled={!scrolledToBottom || !accepted || !signature || !chopReady || submitting}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-[#ffa217] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#f59200] disabled:opacity-50"
                  >
                    {submitting ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <CheckCircle2 size={14} />
                    )}
                    {t("Finalize")}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      // Sign Now is only rendered when there is no drawing, which is
                      // never true while a finalize is running — but the guard is
                      // here so opening the pad cannot become a way to edit the
                      // document out from under an in-flight request.
                      if (submitting) return;
                      setShowSignaturePad(true);
                    }}
                    disabled={!scrolledToBottom}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-[#ffa217] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#f59200] disabled:opacity-50"
                  >
                    {t("Sign Now")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* The RN `signaturePadFullScreenContainer`: mobile has no canvas inside the
          agreement at all, it draws in an overlay over the document and commits
          on its Confirm. Sibling of the agreement dialog rather than a child, so
          dismissing the pad cannot also dismiss the agreement behind it.
          `//` rather than a JSX comment is required inside the parentheses
          below: an expression container is not a valid child position there,
          and a line comment in JSX content position renders as a paragraph. */}
      {showSignaturePad && !submitting && (
        <Modal visible fullScreen onClose={handleAbandonSignature}>
          <div className="flex h-full w-full items-center justify-center p-4">
            <div className="w-full max-w-2xl space-y-4 rounded-2xl bg-white p-5 shadow-xl">
              <SignaturePad onChange={setDraftSignature} />
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={handleAbandonSignature}
                  className="rounded-xl px-4 py-2 text-sm font-bold text-gray-500"
                >
                  {t("Cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleConfirmSignature}
                  disabled={!draftSignature}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[#ffa217] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#f59200] disabled:opacity-50"
                >
                  {t("Confirm")}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default StudentSponsorAgreementModal;
